import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import PropTypes from 'prop-types';

import {redirectToTenant, renewTokens} from '../actions';
import ErrorPage from '../components/error/ErrorPage';
import {
    verifyCookieContainTenantId,
    cigDecode,
    removeTenantLocationLock,
    removeLoginCookie,
    serverLog,
    sendRedirectWithForm,
} from '../common/utils';
import {CIG_SSO, OCI_SSO, PRODUCT_TYPE, APPLICATION_ID, UI_MODE_PARAM} from '../common/constants';
import {numberOfRenewRetried, ociErrorMessage, ociErrorTitle, tokenRenewed} from './selectors';
import Callback from '../components/Callback';


// pass in state.site
const getSite = (site, auth) => {
    if (_.isUndefined(site)) {
        const searchParams = sessionStorage.getItem(CIG_SSO.sessionKey) || verifyCookieContainTenantId();
        if (searchParams === false)
            return;

        try {
            const qStr = JSON.parse(cigDecode(searchParams));
            if (Object.prototype.hasOwnProperty.call(qStr, CIG_SSO.ociSite)) {
                return qStr[CIG_SSO.ociSite];
            }
        }
        catch (e) {
            const logMessage = 'CigSSONav: getSite - Parse JSON error: ' + e +  ' tenant: ' + searchParams;
            const error = {
                category: 'ERROR',
                logMessage: logMessage,
            };
            serverLog(auth, error);
        }
    }
    return site;
};

const getErrorMessage = (site, auth, errorTitle, errorMessage, tenantId) => {
    const ociSite = getSite(site, auth);

    if (!_.isNil(errorMessage) && !_.isEmpty(errorMessage)) {
        const error = {
            category: 'ERROR',
            tenantId: tenantId,
            logMessage: 'CigSSONav: getErrorMessage - Error while try to redirect to ' + ociSite + '. Error - ' + errorMessage + ' ' + errorTitle,
        };
        serverLog(auth, error);
    }

    if (_.isNil(ociSite) || _.isEmpty(ociSite)) {
        return (
            <p id="tenant-nav-error-container">
                Please try again.
            </p>
        );
    }

    return (
        <div id="tenant-nav-error-container">
            You may no longer be authorized to use the tenant.
            <br></br>
            Data Infrastructure Insights: {ociSite}
        </div>
    );
};
getErrorMessage.displayName = 'getErrorMessage';

export function CigSSONav({ auth, errorTitle, errorMessage, numberOfRenewRetried, tokenRenewed, redirectToTenant, renewTokens, sendRedirectWithForm}) {
    const [isError, setIsError] = useState(false);
    const [site, setSite] = useState();
    const [tenantId, setTenantId] = useState();
    let delayError = true;


    const redirectPage = () => {
        let searchParams = sessionStorage.getItem(CIG_SSO.sessionKey) || verifyCookieContainTenantId();
        const logPrefix = 'CigSSONav: ';

        if (searchParams === false || searchParams === 'undefined' || _.includes(searchParams, CIG_SSO.randomKey)) {
            const logMessage = logPrefix + 'redirectPage - Failed to get search params for redirection ';
            const error = {
                category: 'ERROR',
                logMessage: logMessage + (searchParams === false || _.isNil(searchParams) ? searchParams : cigDecode(searchParams))
            };
            sessionStorage.removeItem(CIG_SSO.sessionKey);
            serverLog(auth, error);
            setIsError(true);
        }
        else {
            try {
                const queryString = JSON.parse(cigDecode(searchParams));
                if (Object.prototype.hasOwnProperty.call(queryString, CIG_SSO.tenantId) && Object.prototype.hasOwnProperty.call(queryString, PRODUCT_TYPE)) {
                    if (tokenRenewed !== 'failure') {
                        setSite(queryString[CIG_SSO.ociSite]);
                        setTenantId(queryString[CIG_SSO.tenantId]);
                    }

                    sessionStorage.removeItem(CIG_SSO.sessionKey);
                    sessionStorage.removeItem('redirect');
                    removeTenantLocationLock();
                    removeLoginCookie();

                    if (tokenRenewed === 'failure') {
                        window.CIG_LOG.debug('Cig Nav: Failed to renew token, tokenRenewed is failure. Trigger logout');
                        sendRedirectWithForm('/ui/logout', queryString[CIG_SSO.tenantId], queryString[CIG_SSO.ociSite],
                            Object.prototype.hasOwnProperty.call(queryString, OCI_SSO.ociRedirect) ? queryString[OCI_SSO.ociRedirect] : '',
                            Object.prototype.hasOwnProperty.call(queryString, PRODUCT_TYPE) ? queryString[PRODUCT_TYPE.toString()] : '',
                            Object.prototype.hasOwnProperty.call(queryString, APPLICATION_ID) ? queryString[APPLICATION_ID.toString()] : '');
                    }
                    else {
                        redirectToTenant(window, auth, queryString[CIG_SSO.tenantId], queryString[CIG_SSO.ociSite],
                            Object.prototype.hasOwnProperty.call(queryString, OCI_SSO.ociRedirect) ? queryString[OCI_SSO.ociRedirect] : '',
                            Object.prototype.hasOwnProperty.call(queryString, PRODUCT_TYPE) ? queryString[PRODUCT_TYPE.toString()] : '',
                            Object.prototype.hasOwnProperty.call(queryString, APPLICATION_ID) ? queryString[APPLICATION_ID.toString()] : '',
                            Object.prototype.hasOwnProperty.call(queryString, UI_MODE_PARAM) ? queryString[UI_MODE_PARAM.toString()] : '');
                    }
                }
                else {
                    const logMessage = logPrefix + 'redirectPage - Missing tenantId id or product type ' + cigDecode(searchParams);
                    window.CIG_LOG.error(logMessage);
                    const error = {
                        category: 'ERROR',
                        logMessage: logMessage,
                    };
                    sessionStorage.removeItem(CIG_SSO.sessionKey);
                    serverLog(auth, error);
                    setIsError(true);
                }
            }
            catch (e) {
                const logMessage =  logPrefix + 'redirectPage - Parse JSON error: ' + e +  ' searchParam: ' + searchParams;
                const error = {
                    category: 'ERROR',
                    logMessage: logMessage,
                };
                serverLog(auth, error);
                if (tokenRenewed !== 'failure') {
                    if (delayError) {
                        _.delay(redirectPage, 2000);
                        delayError = false;
                    }
                    else {
                        setIsError(true);
                    }
                }
            }
        }
    };

    // DO NOT add redirectPage to dependency. It is intentional left out.
    useEffect(() => {
        if (!isError && _.isEmpty(errorMessage) && _.isEmpty(tenantId) && numberOfRenewRetried <= 1) {
            if (tokenRenewed === 'success') {
                redirectPage();
            }
            else if (numberOfRenewRetried === 0) {
                renewTokens(0);
            }
            else {
                window.CIG_LOG.debug('Failed to renew access token. Too many requests. Retry...');
                _.delay(renewTokens, 5000, numberOfRenewRetried)
            }
        }
        else if (!_.isEmpty(errorMessage) && tokenRenewed === 'failure') {
            redirectPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isError, errorMessage, tenantId, numberOfRenewRetried, tokenRenewed]);

    return (
        <div>
            {
                (!isError && _.isNil(errorMessage)) && <Callback />
            }
            {
                (isError || (!_.isUndefined(errorMessage) && tokenRenewed !== 'failure')) && (
                    <ErrorPage id="tenant-nav-error" title={'We encountered a problem accessing your Data Infrastructure Insights tenant.'}>
                        { getErrorMessage(site, auth, errorTitle, errorMessage, tenantId) }
                    </ErrorPage>
                )
            }
        </div>
    );
}

CigSSONav.propTypes = {
    auth: PropTypes.object,
    errorMessage: PropTypes.string,
    errorTitle: PropTypes.string,
    redirectToTenant: PropTypes.func.isRequired,
    sendRedirectWithForm: PropTypes.func.isRequired,
    removeTenantLocationLock: PropTypes.func,
    numberOfRenewRetried: PropTypes.number,
    tokenRenewed: PropTypes.string,
    renewTokens: PropTypes.func,

};

CigSSONav.displayName = 'CigSSONav';
function mapStateToProps(state){
    return {
        errorMessage: _.isUndefined(state) ? undefined : ociErrorMessage(state),
        errorTitle: ociErrorTitle(state),
        numberOfRenewRetried: numberOfRenewRetried(state),
        tokenRenewed: tokenRenewed(state),
    }
}
export default connect(
    mapStateToProps,
    {
        redirectToTenant,
        sendRedirectWithForm,
        renewTokens,
    })(CigSSONav);
